import merge from 'lodash/merge'
import includes from 'lodash/includes'
import { useMemo } from 'react'
import { useAllLocalStorage } from '../useStorage/useAllLocalStorage'
import { useHistory } from '../useHistory'
import type { QueryInput } from '../types/QueryInput'
import { requiredCheck } from '@ztechapps/common.utils'
import { useMutation } from '@apollo/client'
import { useDocument } from '../useDocument'
import { logoutQuery, queries } from './graphql'
import type { DocumentNode } from '@apollo/client/core'
import type { User } from '@ztechapps/common.contract'
import { UserStatusEnum } from '@ztechapps/common.contract'

export type UseUserProps = Partial<QueryInput> & {
  userIdKey?: string
  tokenKey?: string
  _id?: string
}

type GetUserStatusResult = {
  loggedIn: boolean
  hasTenant: boolean
  isPending: boolean
  isActive: boolean
  isInActive: boolean
  isSuperAdmin: boolean
}

const getUserStatus = (user: User): GetUserStatusResult => {
  const { tenantId, status, roles = [], _id } = user || {}
  const hasTenant = !!tenantId
  const isPending = status === UserStatusEnum.pending
  const isActive = status === UserStatusEnum.active
  const isInActive = status === UserStatusEnum.inActive
  const loggedIn = !!_id
  const isSuperAdmin = includes(roles, 'superAdmin')
  return {
    loggedIn,
    hasTenant,
    isPending,
    isActive,
    isInActive,
    isSuperAdmin,
  }
}

const useUserRaw = ({
  query,
  _id,
  filter,
  options,
  projection,
  sort,
}: {
  query?: DocumentNode
  _id?: string
  filter?: Record<string, any>
  options?: Record<string, any>
  projection?: Record<string, any>
  sort?: Record<string, any>
}) => {
  query = query || queries.singleConfig.query
  options = merge({ queryOptions: { fetchPolicy: 'cache-first' } }, options)

  const userDocument = useDocument<User>({ query, entity: 'singleUser', _id, filter, options, projection, sort })

  //if there is an error we need to handle it
  // if (userDocument?.error) {
  //   console.log(userDocument?.error, 'so here is the erro')
  // }
  //just remove the document field name and replace it as user
  //userDocument.user = get(userDocument, 'document')
  //userDocument = omit(userDocument, 'document')
  const user = userDocument?.document

  const status = getUserStatus(user)

  const [mutate] = useMutation(logoutQuery)

  const logout = ({ accessToken }) => {
    requiredCheck(accessToken, 'logout.accessToken')

    return mutate({
      variables: { accessToken },
    })
  }

  return {
    user,
    ...userDocument,
    ...status,
    logout,
    getUserStatus,
  }
}

export type UseUserResult = {
  accessToken: string
  logout: (redirectTo?: string | boolean) => void
  getUserStatus: ({ user }: any) => GetUserStatusResult
  loggedIn: boolean
  hasTenant: boolean
  isPending: boolean
  isActive: boolean
  isInActive: boolean
  isSuperAdmin: boolean
  user: {
    _id: string
    tenantId: string
    timeZone: string
    roles: string[]
    status: string
    profile: {
      name: string
      email: string
      picture: string
    }
  }
  loading: boolean
  refetch: () => void
}

/**
 * This is the useUser for web components, that has access to token and userKey
 * It just wraps the useUser for web simplicity
 */
export const useUser = ({
  userIdKey = 'app-login-userId',
  tokenKey = 'app-login-token',
  query,
  filter,
  options,
  projection,
  sort,
}: UseUserProps = {}): UseUserResult => {
  const history = useHistory()
  const localStorage = useAllLocalStorage()
  const _id: string = useMemo(() => localStorage.get(userIdKey), [userIdKey])
  const accessToken = useMemo(() => localStorage.get(tokenKey), [tokenKey])

  const userResult = useUserRaw({ _id, query, filter, options, projection, sort })

  const logout = async (redirectTo) => {
    await userResult?.logout({ accessToken })

    //clean the storage after the logout
    localStorage.remove(tokenKey)
    localStorage.remove(userIdKey)

    //just exit to the function
    if (redirectTo === false) return

    //redirect to the provided URL
    if (redirectTo) {
      return history.push(redirectTo)
    }

    //navigate to the default route
    return history.push('/account/login')
  }

  return { ...userResult, accessToken, logout }
}
